import React, { useMemo } from 'react';
import { BASE_DIMENSIONS } from '../../config/questionnaire';
import {
  ChartBarIcon,
  AnchorIcon,
  TrendingUpIcon,
  CheckCircleIcon,
  ShipIcon,
  TargetIcon,
} from '../icons';

const dimensionIcons = {
  availability: AnchorIcon,
  costs: TrendingUpIcon,
  reliability: CheckCircleIcon,
  port: ShipIcon,
  general: TargetIcon,
};

const methodologyPhases = [
  {
    step: '01',
    title: 'Levantamiento mensual',
    detail: 'Convocamos a responsables logísticos de los principales sectores exportadores y capturamos su percepción del último mes.',
  },
  {
    step: '02',
    title: 'Validación y limpieza',
    detail: 'Automatizamos la detección de respuestas incompletas y luego contrastamos con indicadores operativos para asegurar consistencia.',
  },
  {
    step: '03',
    title: 'Construcción del índice',
    detail: 'Normalizamos cada respuesta a la escala 0–100 y calculamos subíndices ponderados por dimensión.',
  },
  {
    step: '04',
    title: 'Publicación y narrativa',
    detail: 'Integramos hallazgos cuantitativos con verbatims cualitativos antes de entregar informes privados y divulgación pública.',
  },
];

const scoreLegend = [
  { range: '85 – 100', label: 'Excelente', tone: 'from-emerald-400 to-emerald-600' },
  { range: '70 – 84', label: 'Bueno', tone: 'from-lime-400 to-lime-600' },
  { range: '55 – 69', label: 'Aceptable', tone: 'from-amber-300 to-amber-500' },
  { range: '40 – 54', label: 'Alerta', tone: 'from-orange-400 to-orange-600' },
  { range: '0 – 39', label: 'Crítico', tone: 'from-rose-400 to-red-600' },
];

const MethodologyPage = ({ history }) => {
  const coveredSectors = useMemo(() => {
    const submissions = Object.values(history || {}).flatMap((entry) => entry?.sourceSubmissions || []);
    return new Set(
      submissions.map((s) => s?.exportType?.toString().trim()).filter(Boolean),
    ).size;
  }, [history]);
  const activeParticipants = useMemo(() => {
    const submissions = Object.values(history || {}).flatMap((entry) => entry?.sourceSubmissions || []);
    return new Set(
      submissions.map((s) => s?.companyId || s?.companyName?.toString().trim()).filter(Boolean),
    ).size;
  }, [history]);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto">

      {/* Page header */}
      <div>
        <p className="text-[0.6rem] uppercase text-slate-400 mb-1">Metodología</p>
        <h1 className="text-xl font-semibold text-slate-900">Cómo se construye el Barómetro Naviero</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
          Cada mes consolidamos la voz de las empresas exportadoras y la convertimos en indicadores accionables sobre logística, costos y operación portuaria.
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: 'Sectores cubiertos',    value: coveredSectors,    unit: 'sectores reportados',     accent: 'bg-[#004AAD]' },
          { label: 'Participantes activos', value: activeParticipants, unit: 'empresas participantes',  accent: 'bg-emerald-500' },
          { label: 'Escala final',          value: '0 – 100',         unit: 'puntos por período',      accent: 'bg-amber-400' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-slate-200/70 p-5 shadow-sm">
            <div className={`w-6 h-1 rounded-full ${stat.accent} mb-4`} />
            <p className="text-[0.6rem] uppercase text-slate-400 mb-1">{stat.label}</p>
            <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{stat.unit}</p>
          </div>
        ))}
      </div>

      {/* Process steps */}
      <div className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-6 space-y-5">
        <div>
          <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Proceso</p>
          <h2 className="text-lg font-semibold text-slate-900">Del panel al índice</h2>
          <p className="text-sm text-slate-500 mt-1">El flujo combina captura mensual, validación y un modelo de cálculo ponderado para comparar períodos.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {methodologyPhases.map((phase) => (
            <div key={phase.step} className="flex gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#004AAD]/10 text-[#004AAD] text-xs font-bold flex items-center justify-center">
                {phase.step}
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-900">{phase.title}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{phase.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dimensions */}
      <div className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-6 space-y-5">
        <div>
          <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Dimensiones</p>
          <h2 className="text-lg font-semibold text-slate-900">Los cinco pilares que medimos</h2>
          <p className="text-sm text-slate-500 mt-1">Cada pregunta pertenece a uno de los cinco pilares que alimentan el índice general.</p>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          {BASE_DIMENSIONS.map((dimension) => {
            const Icon = dimensionIcons[dimension.id] || ChartBarIcon;
            const likertCount = dimension.questions.filter((q) => q.type === 'likert').length;
            return (
              <div key={dimension.id} className="rounded-xl border border-slate-100 p-5 flex gap-4 hover:border-[#004AAD]/20 transition-colors">
                <span className="flex-shrink-0 p-2.5 rounded-lg bg-[#004AAD]/8 text-[#004AAD]">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{dimension.name}</p>
                    <span className="text-[0.58rem] uppercase text-[#004AAD] bg-[#004AAD]/8 px-2 py-0.5 rounded-full font-medium">
                      {Math.round(dimension.weight * 100)}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">{dimension.questions.length} preguntas · {likertCount} escala Likert</p>
                  <ul className="text-xs text-slate-500 list-disc pl-4 space-y-0.5">
                    {dimension.questions.slice(0, 2).map((q) => (
                      <li key={q.id}>{q.text}</li>
                    ))}
                    {dimension.questions.length > 2 && (
                      <li className="text-slate-400">+{dimension.questions.length - 2} preguntas adicionales</li>
                    )}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Escala + Fórmula */}
      <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-5">
        <div className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-6 space-y-4">
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Escala</p>
            <h3 className="text-lg font-semibold text-slate-900">Interpretación del índice</h3>
            <p className="text-sm text-slate-500 mt-1">Convertimos cada respuesta a 0–100. El índice es la suma ponderada de los cinco pilares.</p>
          </div>
          <div className="space-y-2">
            {scoreLegend.map((band) => (
              <div key={band.range} className="flex items-center gap-4 rounded-xl border border-slate-100 px-4 py-3 bg-slate-50/60">
                <div className={`flex-shrink-0 w-1.5 h-10 rounded-full bg-gradient-to-b ${band.tone}`} />
                <div>
                  <p className="text-sm font-semibold text-slate-800">{band.label}</p>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">{band.range} pts</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200/70 shadow-sm p-6 space-y-4">
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Fórmula</p>
            <h3 className="text-lg font-semibold text-slate-900">¿Cómo se calcula?</h3>
          </div>
          <ol className="space-y-3">
            {[
              'Las respuestas 1–5 se transforman linealmente al rango 0–100. Las binarias se interpretan como 0 o 100 según el sentido de la pregunta.',
              'Se promedian por dimensión con ponderaciones robustas antes de la primera corrida: Disponibilidad 25%, Costos 20%, Cumplimiento Naviero 35%, Operación Portuaria 10% y Experiencia General 10%.',
              'Se suman los cinco subíndices ponderados para obtener el índice global del período.',
            ].map((text, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-600">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#004AAD]/10 text-[#004AAD] text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{text}</span>
              </li>
            ))}
          </ol>
          <div className="rounded-xl bg-slate-50 border border-slate-200/70 px-4 py-3">
            <p className="text-[0.58rem] uppercase text-slate-400 mb-1">Fórmula simplificada</p>
            <p className="text-sm font-semibold text-slate-900 font-mono">
              Índice = Σ (Pilar<sub>i</sub> × Peso<sub>i</sub>)
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MethodologyPage;
